import React, { useEffect, useState } from 'react';
import {
  LabelTemplate,
  LabelElement,
  FieldData,
  mmToPx,
  ptToMm,
  SCREEN_DPI,
  ShapeElement,
  ImageElement,
  IconElement,
} from '@/types/label';
import { generateBarcodeDataUrl, generateQRCodeDataUrl } from '@/lib/barcodes';
import { icons as LucideIcons } from 'lucide-react';

interface LabelThumbnailProps {
  template: LabelTemplate;
  fieldData: FieldData;
  scale?: number;
  className?: string;
}

export const LabelThumbnail: React.FC<LabelThumbnailProps> = ({
  template,
  fieldData,
  scale = 1,
  className = '',
}) => {
  const [barcodeImages, setBarcodeImages] = useState<Record<string, string>>({});

  const resolveContent = (content: string) =>
    content.replace(/\{\{(\w+)\}\}/g, (match, field) => fieldData[field] || match);

  // Generate barcode/qr images
  useEffect(() => {
    const generate = async () => {
      const images: Record<string, string> = {};
      for (const el of template.elements) {
        if (el.visible === false) continue;
        const content = el.content ? resolveContent(el.content) : '';
        if (!content) continue;
        try {
          if (el.type === 'barcode') {
            const url = await generateBarcodeDataUrl(
              content, 100, 40, el.includeText ?? true,
              el.barScale || 2, el.barcodeType || 'code128',
              el.barHeightRatio || 0.8, el.quietZone ?? 2,
              el.foregroundColor, el.backgroundColor, el.textSize,
            );
            if (url) images[el.id] = url;
          } else if (el.type === 'qrcode') {
            const url = await generateQRCodeDataUrl(content, 100, el.foregroundColor, el.backgroundColor);
            if (url) images[el.id] = url;
          }
        } catch { /* skip failed barcodes */ }
      }
      setBarcodeImages(images);
    };
    generate();
  }, [template.elements, fieldData]);

  const widthPx = mmToPx(template.width, SCREEN_DPI) * scale;
  const heightPx = mmToPx(template.height, SCREEN_DPI) * scale;

  const renderElement = (element: LabelElement) => {
    if (element.visible === false) return null;
    const x = mmToPx(element.position.x, SCREEN_DPI) * scale;
    const y = mmToPx(element.position.y, SCREEN_DPI) * scale;
    const width = mmToPx(element.size.width, SCREEN_DPI) * scale;
    const height = mmToPx(element.size.height, SCREEN_DPI) * scale;
    const content = element.content ? resolveContent(element.content) : '';

    const rotation = element.rotation || 0;
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: x,
      top: y,
      width,
      height,
      pointerEvents: 'none',
      overflow: 'hidden',
      transform: rotation ? `rotate(${rotation}deg)` : undefined,
      transformOrigin: 'center center',
    };

    if (element.type === 'text') {
      const fontSizePx = mmToPx(ptToMm(element.fontSize), SCREEN_DPI) * scale;
      return (
        <div key={element.id} style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start',
          fontSize: fontSizePx,
          fontFamily: element.fontFamily,
          fontWeight: element.fontWeight,
          color: element.color || '#000000',
          whiteSpace: 'nowrap',
        }}>
          {content}
        </div>
      );
    }

    if (element.type === 'barcode' || element.type === 'qrcode') {
      const src = barcodeImages[element.id];
      return (
        <div key={element.id} style={baseStyle}>
          {src ? (
            <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: 'hsl(var(--muted))' }} />
          )}
        </div>
      );
    }

    if (element.type === 'image') {
      const img = element as ImageElement;
      return (
        <div key={element.id} style={{ ...baseStyle, borderRadius: img.borderRadius || 0 }}>
          {img.src ? (
            <img src={img.src} alt="" style={{ width: '100%', height: '100%', objectFit: img.objectFit || 'contain' }} />
          ) : null}
        </div>
      );
    }

    if (element.type === 'shape') {
      const s = element as ShapeElement;
      return (
        <div key={element.id} style={{ ...baseStyle, backgroundColor: 'transparent' }}>
          {s.shapeType === 'rectangle' && (
            <div style={{ width: '100%', height: '100%', backgroundColor: s.fillColor || 'transparent', border: `${s.strokeWidth || 1}px solid ${s.strokeColor || '#000'}`, borderRadius: s.borderRadius || 0 }} />
          )}
          {s.shapeType === 'circle' && (
            <div style={{ width: '100%', height: '100%', backgroundColor: s.fillColor || 'transparent', border: `${s.strokeWidth || 1}px solid ${s.strokeColor || '#000'}`, borderRadius: '50%' }} />
          )}
        </div>
      );
    }

    if (element.type === 'icon') {
      const iconEl = element as IconElement;
      const IconComp = (LucideIcons as any)[iconEl.iconName?.replace(/-([a-z])/g, (_: any, c: string) => c.toUpperCase()).replace(/^./, (c: string) => c.toUpperCase())];
      return (
        <div key={element.id} style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {IconComp && <IconComp style={{ width: '80%', height: '80%', color: iconEl.color || '#000000' }} />}
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: widthPx,
        height: heightPx,
        background: 'white',
        border: '1px solid hsl(var(--border))',
        borderRadius: 4,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {template.elements.map(renderElement)}
    </div>
  );
};